import { useRef } from "react";
import Color from "color";
import { Chart as ChartJS, CategoryScale, ChartData } from "chart.js/auto";
import Annotation from "chartjs-plugin-annotation";
import { Bar } from "react-chartjs-2";
import styled from "styled-components";
import { ColorAA } from "../constants";
import { useAATheme } from "../AAThemeContext";

ChartJS.register(CategoryScale);
ChartJS.register(Annotation);

const BarPlotContainer = styled.div`
  width: 100%;
  height: 80px;
  padding: 10px 10px 0px 10px;
  margin-bottom: 20px;
  background-color: var(--aa-surface-bg);
  border-radius: 6px;
`;

const BarPlot = ({
  data,
  momentId,
  startTime,
  endTime,
  selectedTimestamp,
  onClickElement,
}: {
  data: ChartData<"bar", object[], string>;
  momentId?: number | null;
  startTime?: number;
  endTime?: number;
  selectedTimestamp?: number;
  onClickElement?: (index: number) => void;
}) => {
  const chartRef = useRef();
  const handleClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (chartRef.current) {
      const chart = chartRef.current as ChartJS;
      const element = chart.getElementsAtEventForMode(
        e.nativeEvent,
        "nearest",
        {
          intersect: false,
        },
        false
      );
      if (element?.[0]?.index !== undefined) {
        onClickElement?.(element[0].index);
      }
    }
  };

  const { isDarkMode } = useAATheme();
  const textColor = isDarkMode ? ColorAA.lightBeige : ColorAA.lightBlack;
  const mutedColor = Color(isDarkMode ? ColorAA.beige : ColorAA.grey)
    .alpha(0.8)
    .hexa();
  const tooltipBg = isDarkMode ? ColorAA.lightBlack : ColorAA.white;
  const codePink = ColorAA.pink;

  return (
    <BarPlotContainer>
      <Bar
        ref={chartRef}
        onClick={handleClick}
        data={data}
        options={{
          maintainAspectRatio: false,
          interaction: {
            mode: "nearest",
            intersect: false,
          },
          scales: {
            x: {
              type: "time",
              min: startTime,
              max: endTime,
              time: {
                minUnit: "second",
              },
              ticks: {
                autoSkip: true,
                display: true,
                maxTicksLimit: 6,
                align: "inner",
                includeBounds: true,
                color: mutedColor,
              },
              border: {
                display: false,
              },
              grid: {
                tickLength: 0,
                display: false,
              },
            },
            y: {
              ticks: {
                display: false,
              },
              grid: {
                display: false,
              },
              border: {
                display: false,
              },
            },
          },
          plugins: {
            annotation: selectedTimestamp
              ? {
                  annotations: {
                    selectedMoment: {
                      type: "line",
                      xMin: selectedTimestamp,
                      xMax: selectedTimestamp,
                      borderWidth: 6,
                      borderColor: isDarkMode
                        ? ColorAA.yellow
                        : ColorAA.lightBlack,
                      label: {
                        content: `Moment ${momentId}`,
                        display: momentId !== null,
                        position: "center",
                        color: isDarkMode ? ColorAA.lightBlack : ColorAA.yellow,
                        backgroundColor: isDarkMode
                          ? ColorAA.yellow
                          : ColorAA.lightBlack,
                      },
                    },
                  },
                }
              : undefined,
            legend: {
              display: false,
              position: "right",
              labels: {
                font: {
                  family: "Helvetica Neue",
                  size: 14,
                },
              },
            },
            tooltip: {
              intersect: false,
              enabled: true,
              yAlign: "center",
              displayColors: false,
              titleColor: mutedColor,
              borderWidth: 0.4,
              borderColor: mutedColor,
              bodyColor: textColor,
              footerColor: codePink,
              footerFont: { size: 12, family: "Monospace", weight: "normal" },
              backgroundColor: tooltipBg,
              titleFont: { size: 14 },
              bodyFont: { size: 14 },
              callbacks: {
                label: (tooltipItem) => {
                  const rawItem = tooltipItem.raw as {
                    momentId?: number;
                    foreignSessionId?: string;
                  };
                  return rawItem?.momentId ? `Moment ${rawItem.momentId}` : "";
                },
                footer: (tooltipItem) => {
                  const rawItem = tooltipItem[0]?.raw as {
                    momentId?: number;
                    foreignSessionId?: string;
                  };
                  return rawItem?.foreignSessionId;
                },
              },
            },
          },
        }}
      />
    </BarPlotContainer>
  );
};

export default BarPlot;
